import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FaHome, FaRedo, FaShareAlt, FaStar, FaArrowLeft } from 'react-icons/fa';
import toast from 'react-hot-toast';
import Navbar from '../components/Layout/Navbar';
import Timer from '../components/UI/Timer';
import QuestionCard from '../components/Quiz/QuestionCard';
import ScorePopup from '../components/Quiz/ScorePopup';
import Button from '../components/UI/Button';
import api from '../api/axios';
import useTimer from '../hooks/useTimer';
import useSound from '../hooks/useSound';
import { useAuth } from '../context/AuthContext';
import LevelUpModal from '../components/UI/LevelUpModal';
import useLevelUp from '../hooks/useLevelUp';

const TIME_PER_QUESTION = 20;
const TOTAL_QUESTIONS   = 10;

function shuffleOptions(question) {
  const rawChoix = question.choix || question.options || question.reponses || [];
  const choix = Array.isArray(rawChoix) ? rawChoix : Object.values(rawChoix);
  const correctIdx = question.bonne_reponse ?? question.reponseCorrecte ?? question.correctAnswer ?? 0;
  const correctText = choix[correctIdx];
  const shuffled = [...choix].sort(() => Math.random() - 0.5);
  return {
    ...question,
    options: shuffled,
    reponseCorrecte: shuffled.indexOf(correctText),
  };
}

function ThemeQuiz() {
  const navigate = useNavigate();
  const { themeId } = useParams();
  const { user, updateUser } = useAuth();
  const { checkLevelUp, levelUpData, clearLevelUp } = useLevelUp();
  const { playCorrect, playIncorrect, playTick, playVictory } = useSound();

  const isPremium = user?.plan === 'premium' || user?.role === 'admin';

  // États
  const [theme, setTheme]             = useState(null);
  const [questions, setQuestions]     = useState([]);
  const [gameId, setGameId]           = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [revealed, setRevealed]       = useState(false);
  const [score, setScore]             = useState(0);
  const [streak, setStreak]           = useState(0);
  const [showScorePopup, setShowScorePopup] = useState(false);
  const [lastPoints, setLastPoints]   = useState(25);
  const [gameOver, setGameOver]       = useState(false);
  const [loading, setLoading]         = useState(true);
  const [results, setResults]         = useState([]);
  const [error, setError]             = useState(null);

  const nextQuestionRef = useRef(null);

  const handleTimeUp = useCallback(() => {
    if (!revealed) {
      playIncorrect();
      setRevealed(true);
      setSelectedAnswer(null);
      setStreak(0);
      setResults((r) => [...r, { correct: false, points: 0 }]);
      // Pas d'auto-avance : le joueur doit cliquer CONTINUER
    }
  }, [revealed]);

  const { timeLeft, start, pause, reset } = useTimer(TIME_PER_QUESTION, handleTimeUp);

  useEffect(() => {
    if (timeLeft <= 5 && timeLeft > 0 && !revealed) {
      playTick();
    }
  }, [timeLeft, revealed]);

  // Chargement des questions (vérification premium côté serveur)
  useEffect(() => {
    api.post(`/games/theme/${themeId}/start`, { nbQuestions: TOTAL_QUESTIONS })
      .then(({ data }) => {
        setTheme(data.theme || null);
        setGameId(data.gameId || null);
        const rawQ = data.questions || [];
        if (rawQ.length === 0) {
          setError('no_questions');
          return;
        }
        setQuestions(rawQ.map(shuffleOptions));
      })
      .catch((err) => {
        if (err.response?.status === 403) {
          setError('premium');
        } else if (err.response?.status === 404) {
          setError('not_found');
        } else {
          setError('server');
        }
      })
      .finally(() => setLoading(false));
  }, [themeId]);

  // Démarrer le timer à chaque nouvelle question
  useEffect(() => {
    if (!loading && questions.length > 0 && !gameOver && !error) {
      reset(TIME_PER_QUESTION);
      start();
    }
  }, [loading, currentIndex, gameOver, error]);

  const handleAnswer = useCallback(async (answerIndex) => {
    if (revealed || selectedAnswer !== null) return;

    pause();
    setSelectedAnswer(answerIndex);
    setRevealed(true);

    const q = questions[currentIndex];
    const correctIdx = q.reponseCorrecte ?? 0;
    const isCorrect  = answerIndex === correctIdx;

    if (isCorrect) {
      const streakBonus = Math.min(streak, 4);
      const timeBonus   = Math.floor((timeLeft / TIME_PER_QUESTION) * 10);
      const points      = 25 + streakBonus * 5 + timeBonus;
      setScore((s) => s + points);
      setStreak((s) => s + 1);
      setLastPoints(points);
      setShowScorePopup(true);
      playCorrect();
      setTimeout(() => setShowScorePopup(false), 1200);
      setResults((r) => [...r, { correct: true, points, timeBonus }]);
    } else {
      setStreak(0);
      playIncorrect();
      setResults((r) => [...r, { correct: false, points: 0 }]);
    }

    // Enregistrer la réponse (best effort)
    try {
      await api.post('/games/solo/answer', {
        gameId,
        questionId: q.id || q._id,
        answer:     answerIndex,
        timeMs:     (TIME_PER_QUESTION - timeLeft) * 1000,
        currentStreak: streak,
      });
    } catch { /* silencieux */ }
  }, [revealed, selectedAnswer, questions, currentIndex, streak, timeLeft, pause, gameId]);

  const nextQuestion = useCallback(async () => {
    if (currentIndex + 1 >= Math.min(TOTAL_QUESTIONS, questions.length)) {
      pause();
      playVictory();
      setGameOver(true);
      if (user) {
        const xpGained = Math.round(score / 10);
        try {
          const { data } = await api.post('/games/solo/end', { gameId, score });
          if (data.user) {
            const merged = { ...user, ...data.user };
            const updatedUser = checkLevelUp(merged, xpGained);
            updateUser(updatedUser || merged);
          }
        } catch {
          const updatedUser = checkLevelUp(user, xpGained);
          updateUser({ ...(updatedUser || user), coins: (user.coins || 0) + Math.round(score / 5) });
        }
      }
    } else {
      setCurrentIndex((i) => i + 1);
      setSelectedAnswer(null);
      setRevealed(false);
    }
  }, [currentIndex, questions.length, pause, score, user, updateUser, gameId, checkLevelUp, playVictory]);

  nextQuestionRef.current = nextQuestion;

  const handleReplay = async () => {
    setScore(0);
    setStreak(0);
    setCurrentIndex(0);
    setGameOver(false);
    setSelectedAnswer(null);
    setRevealed(false);
    setResults([]);
    setGameId(null);
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.post(`/games/theme/${themeId}/start`, { nbQuestions: TOTAL_QUESTIONS });
      setTheme(data.theme || theme);
      setGameId(data.gameId || null);
      const rawQ = data.questions || [];
      if (rawQ.length === 0) { setError('no_questions'); return; }
      setQuestions(rawQ.map(shuffleOptions));
    } catch (err) {
      if (err.response?.status === 403) setError('premium');
      else setError('server');
    } finally {
      setLoading(false);
    }
  };

  // ── Écran d'erreur ──────────────────────────────────────────
  if (!loading && error) {
    return (
      <div className="app-container flex flex-col min-h-screen">
        <Navbar showBack onBack={() => navigate('/themes')} title="THÈMES" />
        <div className="flex flex-col items-center justify-center flex-1 px-6 gap-5">
          <div className="text-6xl">
            {error === 'premium' ? '⭐' : error === 'no_questions' ? '❓' : '⚠️'}
          </div>
          <div className="text-center">
            <h2
              className="text-2xl font-black text-white uppercase tracking-wider mb-2"
              style={{ fontFamily: "'Rajdhani', sans-serif" }}
            >
              {error === 'premium'      && 'Accès Premium requis'}
              {error === 'no_questions' && 'Pas encore de questions'}
              {error === 'not_found'    && 'Thème introuvable'}
              {error === 'server'       && 'Erreur de connexion'}
            </h2>
            <p className="text-white/50 text-sm leading-relaxed">
              {error === 'premium'      && 'Ce thème est réservé aux membres Premium. Passe Premium pour y accéder.'}
              {error === 'no_questions' && 'Aucune question n\'a encore été ajoutée à ce thème. Reviens bientôt !'}
              {error === 'not_found'    && 'Ce thème n\'existe pas ou n\'est plus disponible.'}
              {error === 'server'       && 'Impossible de charger les questions. Vérifie ta connexion.'}
            </p>
          </div>
          <div className="flex flex-col gap-3 w-full max-w-xs">
            {error === 'premium' && (
              <Button variant="gold" size="lg" fullWidth onClick={() => navigate('/shop')}>
                <FaStar size={14} />
                Passer Premium
              </Button>
            )}
            <Button variant="outline" size="md" fullWidth onClick={() => navigate('/themes')}>
              <FaArrowLeft size={14} />
              Retour aux thèmes
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ── Chargement ──────────────────────────────────────────────
  if (loading) {
    return (
      <div className="app-container flex flex-col min-h-screen">
        <Navbar showBack onBack={() => navigate('/themes')} title="THÈMES" />
        <div className="flex items-center justify-center flex-1">
          <div className="flex flex-col items-center gap-4">
            <div className="w-16 h-16 rounded-full border-4 border-qfl-gold border-t-transparent animate-spin" />
            <p className="text-qfl-gold font-bold tracking-widest" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
              CHARGEMENT...
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ── Fin de partie ────────────────────────────────────────────
  if (gameOver) {
    return (
      <ThemeGameOverScreen
        score={score}
        results={results}
        theme={theme}
        onReplay={handleReplay}
        onHome={() => navigate('/themes')}
      />
    );
  }

  const currentQuestion = questions[currentIndex];

  return (
    <div className="app-container flex flex-col min-h-screen" style={{ paddingBottom: '20px' }}>
      {/* Header thème */}
      <div
        className="flex items-center gap-3 px-4 py-3"
        style={{
          background: theme?.bg_color ? `${theme.bg_color}cc` : 'rgba(0,0,0,0.3)',
          borderBottom: `1px solid ${theme?.color || '#FFB81C'}33`,
        }}
      >
        <button
          onClick={() => navigate('/themes')}
          className="w-8 h-8 rounded-full flex items-center justify-center"
          style={{ background: 'rgba(255,255,255,0.1)' }}
        >
          <FaArrowLeft size={14} className="text-white/70" />
        </button>
        <div
          className="w-8 h-8 rounded-lg overflow-hidden flex-shrink-0 flex items-center justify-center"
          style={{ background: theme?.bg_color || '#003DA5' }}
        >
          {theme?.image
            ? <img src={`/themes/${theme.image}`} alt={theme.label} className="w-full h-full object-cover" />
            : <span className="text-lg">{theme?.icon || '⚽'}</span>
          }
        </div>
        <div className="flex-1 min-w-0">
          <p
            className="text-white font-black uppercase tracking-wider text-sm truncate"
            style={{ fontFamily: "'Rajdhani', sans-serif" }}
          >
            {theme?.label || 'Thème Premium'}
          </p>
          <p className="text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>
            Question {currentIndex + 1}/{Math.min(TOTAL_QUESTIONS, questions.length)}
          </p>
        </div>
        <div
          className="px-2 py-1 rounded-full text-xs font-black uppercase tracking-wider"
          style={{
            background: 'rgba(255,184,28,0.15)',
            border: '1px solid rgba(255,184,28,0.3)',
            color: '#FFB81C',
            fontFamily: "'Rajdhani', sans-serif",
          }}
        >
          ⭐ Premium
        </div>
      </div>

      {/* Timer */}
      <div className="flex justify-center py-4 relative">
        <Timer
          timeLeft={timeLeft}
          totalTime={TIME_PER_QUESTION}
          size={90}
          color={theme?.color}
        />
        {streak > 1 && (
          <motion.div
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            className="absolute right-6 top-4 flex flex-col items-center"
          >
            <span className="text-orange-400 text-2xl">🔥</span>
            <span className="text-orange-400 font-bold text-xs" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
              x{streak}
            </span>
          </motion.div>
        )}
      </div>

      {/* Question */}
      <div className="flex-1 px-4 overflow-y-auto pb-4 relative">
        <AnimatePresence mode="wait">
          <QuestionCard
            key={currentIndex}
            question={currentQuestion}
            questionIndex={currentIndex}
            totalQuestions={Math.min(TOTAL_QUESTIONS, questions.length)}
            selectedAnswer={selectedAnswer}
            correctAnswer={currentQuestion?.reponseCorrecte}
            revealed={revealed}
            onAnswer={handleAnswer}
            disabled={revealed}
            eliminatedOptions={[]}
          />
        </AnimatePresence>

        <ScorePopup
          points={lastPoints}
          visible={showScorePopup}
          streakBonus={streak > 2}
        />
      </div>

      {/* Bouton continuer */}
      <AnimatePresence>
        {revealed && (
          <motion.div
            className="px-4 py-3"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
          >
            <Button variant="gold" size="lg" fullWidth onClick={nextQuestion}>
              {currentIndex + 1 >= Math.min(TOTAL_QUESTIONS, questions.length)
                ? 'VOIR LES RÉSULTATS'
                : 'CONTINUER'}
            </Button>
          </motion.div>
        )}
      </AnimatePresence>

      {levelUpData && (
        <LevelUpModal
          visible={!!levelUpData}
          newLevel={levelUpData.newLevel}
          coinsEarned={levelUpData.coinsEarned}
          unlockedFeature={levelUpData.unlockedFeature}
          onClose={clearLevelUp}
        />
      )}
    </div>
  );
}

// ─── Écran de fin de partie thème ────────────────────────────
function ThemeGameOverScreen({ score, results, theme, onReplay, onHome }) {
  const correctCount = results.filter((r) => r.correct).length;
  const total        = results.length;
  const percentage   = total > 0 ? Math.round((correctCount / total) * 100) : 0;

  const getRating = () => {
    if (percentage >= 90) return { label: 'Légende',          color: '#FFB81C', icon: '👑' };
    if (percentage >= 70) return { label: 'Expert',            color: '##003DA5', icon: '⭐' };
    if (percentage >= 50) return { label: 'Bien joué',         color: '#3b82f6', icon: '👍' };
    return                       { label: 'Continue !',        color: '#94a3b8', icon: '📚' };
  };
  const rating = getRating();

  const handleShare = async () => {
    const text = `${rating.icon} Quiz ${theme?.label || 'Thème'} — ${score} pts !\n✅ ${correctCount}/${total} (${percentage}%)\n⚽ Joue sur OM QUIZ !`;
    if (navigator.share) {
      try { await navigator.share({ title: 'OM QUIZ', text }); } catch (_) {}
    } else {
      try {
        await navigator.clipboard.writeText(text);
        toast.success('Score copié !');
      } catch (_) {}
    }
  };

  return (
    <motion.div
      className="app-container flex flex-col min-h-screen px-5 pt-6 pb-5"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
    >
      {/* Thème badge */}
      <div className="flex justify-center mb-3">
        <div
          className="flex items-center gap-2 px-4 py-2 rounded-full"
          style={{
            background: theme?.bg_color ? `${theme.bg_color}cc` : 'rgba(255,184,28,0.1)',
            border: `1px solid ${theme?.color || '#FFB81C'}44`,
          }}
        >
          {theme?.image
            ? <img src={`/themes/${theme.image}`} alt={theme?.label} className="w-6 h-6 rounded object-cover" />
            : <span className="text-lg">{theme?.icon || '⚽'}</span>
          }
          <span
            className="text-white font-black text-sm uppercase tracking-wider"
            style={{ fontFamily: "'Rajdhani', sans-serif" }}
          >
            {theme?.label || 'Thème Premium'}
          </span>
        </div>
      </div>

      {/* Résultat */}
      <div className="flex flex-col items-center pb-2">
        <motion.div
          initial={{ scale: 0, rotate: -180 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ type: 'spring', stiffness: 200, delay: 0.2 }}
          className="text-5xl mb-2"
        >
          {rating.icon}
        </motion.div>
        <h2
          className="text-3xl font-black uppercase tracking-widest"
          style={{ fontFamily: "'Rajdhani', sans-serif", color: rating.color }}
        >
          {rating.label}
        </h2>
      </div>

      {/* Score */}
      <motion.div
        className="bg-black/40 rounded-3xl p-4 border border-white/10 mb-3 text-center"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
      >
        <div className="text-white/60 text-sm mb-1" style={{ fontFamily: "'Rajdhani', sans-serif" }}>SCORE FINAL</div>
        <motion.div
          className="text-5xl font-black text-qfl-gold"
          style={{ fontFamily: "'Rajdhani', sans-serif" }}
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.4, type: 'spring' }}
        >
          {score}
        </motion.div>
        <div className="text-white/50 text-sm mt-1">points</div>

        <div className="flex justify-around mt-5">
          <div className="flex flex-col items-center">
            <span className="text-2xl font-black text-qfl-green" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
              {correctCount}/{total}
            </span>
            <span className="text-white/50 text-xs">Correctes</span>
          </div>
          <div className="w-px bg-white/10" />
          <div className="flex flex-col items-center">
            <span className="text-2xl font-black text-qfl-gold" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
              {percentage}%
            </span>
            <span className="text-white/50 text-xs">Précision</span>
          </div>
          <div className="w-px bg-white/10" />
          <div className="flex flex-col items-center">
            <span className="text-2xl font-black text-white" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
              {results.reduce((s, r) => s + (r.timeBonus || 0), 0)}
            </span>
            <span className="text-white/50 text-xs">Bonus temps</span>
          </div>
        </div>
      </motion.div>

      {/* Récap */}
      <div className="flex gap-2 mb-4 flex-wrap justify-center">
        {results.map((r, i) => (
          <motion.div
            key={i}
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.05 * Math.min(i, 20), type: 'spring', stiffness: 300 }}
            style={{
              width: 32, height: 32,
              borderRadius: '50%',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontFamily: "'Rajdhani', sans-serif",
              fontWeight: 900,
              fontSize: '0.75rem',
              background: r.correct ? 'rgba(34,197,94,0.25)' : 'rgba(239,68,68,0.25)',
              border: `2px solid ${r.correct ? '##003DA5' : '#ef4444'}`,
              color: r.correct ? '##003DA5' : '#ef4444',
            }}
          >
            {i + 1}
          </motion.div>
        ))}
      </div>

      {/* Boutons */}
      <div className="flex flex-col gap-3 mt-auto">
        <motion.button
          onClick={handleShare}
          whileTap={{ scale: 0.97 }}
          className="w-full py-3.5 rounded-2xl font-bold text-base uppercase tracking-wider flex items-center justify-center gap-2"
          style={{
            background: 'linear-gradient(135deg, #1d9bf0, #1a8cd8)',
            boxShadow: '0 4px 15px rgba(29,155,240,0.4)',
            fontFamily: "'Rajdhani', sans-serif",
            color: '#fff',
          }}
        >
          <FaShareAlt size={16} />
          PARTAGER MON SCORE
        </motion.button>

        <div className="flex gap-3">
          <Button variant="gold" size="lg" fullWidth onClick={onReplay}>
            <FaRedo size={16} />
            REJOUER
          </Button>
          <Button variant="outline" size="md" fullWidth onClick={onHome}>
            <FaHome size={16} />
            THÈMES
          </Button>
        </div>
      </div>
    </motion.div>
  );
}

export default ThemeQuiz;


